import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button, Col, Form, Row} from "react-bootstrap";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {UsacmContext} from "../App";
import {checkoutCourseCart} from "../api/CourseApi";
import {forceHideBusy, forceShowBusy} from "../utils/busyUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {getConfLabel} from "../utils/usacmUtils";
import {CONF_LABEL_KEY_COURSE_CHECKOUT_CONTENT} from "../constants";

/**
 * The payment step of the cart.  A total over zero is paid with Stripe - the order is completed when Stripe says
 * it is paid, which is checked on the order screen Stripe sends the user back to.  A zero total (free prices which
 * need review, or a coupon which takes off everything) is submitted without Stripe.
 */
export function CourseCheckout({cart, onCancel}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const navigate = useNavigate();
  const [stripePromise, setStripePromise] = useState(null);
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);
  const checkoutHtml = getConfLabel(conf, CONF_LABEL_KEY_COURSE_CHECKOUT_CONTENT);
  const totalAmount = cart.amount;
  const needsReview = cart.items.some(item => item.course_price?.requires_review);

  useEffect(() => {
    if (conf && totalAmount > 0) {
      setStripePromise(loadStripe(conf.stripe_public_api_key));
    }
  }, [conf, totalAmount]);

  const stripeOptions = {
    mode: 'payment',
    amount: totalAmount,
    currency: 'usd',
  };

  function submitFreeOrder() {
    // This prevents double clicks submitting the order twice
    if (sending) {
      return;
    }
    setSending(true);
    checkoutCourseCart(totalAmount, (status, data, newErrors) => {
      if (status === 200) {
        navigate(`/course-order?id=${data.course_order_id}`);
      } else {
        setErrors(newErrors);
        setSending(false);
      }
    });
  }

  return (
    <div className="mt-4">
      <div className="section-header">Payment Details</div>

      {checkoutHtml && <div className="mb-3" dangerouslySetInnerHTML={{__html: checkoutHtml}}/>}

      {totalAmount > 0 && stripePromise &&
        <Elements stripe={stripePromise} options={stripeOptions}>
          <CourseCheckoutStripe totalAmount={totalAmount} onCancel={onCancel}/>
        </Elements>
      }

      {totalAmount === 0 &&
        <>
          <div className="mb-3">
            There is nothing to pay.{needsReview && ' Your order will be sent for review.'}
          </div>
          {fieldHasErrors(errors, '') &&
            <Row className="mt-3">
              <Col className="text-center mb-3 usacm-error-message">
                {getErrorMessageForField(errors, '')}
              </Col>
            </Row>
          }
          <Row>
            <Col className="usacm-button-row">
              <Button variant="secondary" onClick={() => onCancel()}>Back</Button>
              <Button disabled={sending} onClick={() => submitFreeOrder()}>{needsReview ? 'Submit for Review' : 'Complete Order'}</Button>
            </Col>
          </Row>
        </>
      }
    </div>
  );
}

/**
 * The Stripe card form.  Must be inside <Elements>.
 */
function CourseCheckoutStripe({totalAmount, onCancel}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const stripe = useStripe();
  const elements = useElements();
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);

  function handleError(errMsg) {
    setErrors([{'message': errMsg, 'fields': ['']}]);
  }

  const submitPayment = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (!stripe) {
      console.warn("Unable to submit pay as Stripe.js hasn't loaded yet");
      return;
    }
    // This prevents double clicks on the Pay button
    if (sending) {
      return;
    }
    setSending(true);
    forceShowBusy();
    const {error: submitError} = await elements.submit();
    if (submitError) {
      handleError(submitError.message);
      forceHideBusy();
      setSending(false);
      return;
    }

    checkoutCourseCart(totalAmount, (status, data, newErrors) => {
      if (status === 200) {
        stripe.confirmPayment({
          elements,
          clientSecret: data.stripe_client_secret,
          confirmParams: {
            // Stripe adds the payment_intent and redirect_status to this
            return_url: `${conf.conf_url}/course-order?id=${data.course_order_id}`,
          },
        }).then(response => {
          if (response && response.error) {
            handleError(response.error.message);
            forceHideBusy();
          }
          // On success Stripe redirects us, so the busy overlay stays up
          setSending(false);
        });
      } else {
        setErrors(newErrors);
        forceHideBusy();
        setSending(false);
      }
    });
  };

  return (
    <Form onSubmit={submitPayment}>
      <PaymentElement/>

      {fieldHasErrors(errors, '') &&
        <Row className="mt-3">
          <Col className="text-center mb-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row">
          <Button variant="secondary" onClick={() => onCancel()}>Back</Button>
          <Button type="submit" disabled={sending}>Pay</Button>
        </Col>
      </Row>
    </Form>
  );
}
